import {
  COOKIE_CONSENT_CHANGED_EVENT,
  COOKIE_CONSENT_STORAGE_KEY,
  COOKIE_CONSENT_TTL_MS,
  OPTIONAL_COOKIE_CONSENT_ITEM_IDS,
  createDefaultCookieConsentChoices,
  isOptionalCookieConsentItemId,
  type CookieConsentChoices,
} from '@/lib/analytics/cookieConsentCatalog'

const STORAGE_KEY = COOKIE_CONSENT_STORAGE_KEY

type StoredCookieConsent = {
  expiresAt: number
  choices: CookieConsentChoices
}

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

function normalizeChoices(
  raw: Partial<Record<string, boolean>> | undefined,
): CookieConsentChoices | null {
  if (!raw) return null

  const choices = createDefaultCookieConsentChoices()

  for (const itemId of OPTIONAL_COOKIE_CONSENT_ITEM_IDS) {
    if (typeof raw[itemId] === 'boolean') {
      choices[itemId] = raw[itemId]
    }
  }

  return choices
}

export function readStoredCookieConsent(): StoredCookieConsent | null {
  if (!isBrowser()) return null

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as StoredCookieConsent
    if (!parsed?.expiresAt || !parsed?.choices) return null
    if (Date.now() > parsed.expiresAt) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }

    const choices = normalizeChoices(parsed.choices)
    if (!choices) return null

    return {
      expiresAt: parsed.expiresAt,
      choices,
    }
  } catch {
    return null
  }
}

export function hasValidCookieConsent(): boolean {
  return readStoredCookieConsent() !== null
}

export function getCookieConsentChoices(): CookieConsentChoices {
  return readStoredCookieConsent()?.choices ?? createDefaultCookieConsentChoices()
}

export function saveCookieConsent(choices: CookieConsentChoices): void {
  if (!isBrowser()) return

  const payload: StoredCookieConsent = {
    expiresAt: Date.now() + COOKIE_CONSENT_TTL_MS,
    choices,
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: choices }))
}

export function clearCookieConsent(): void {
  if (!isBrowser()) return
  window.localStorage.removeItem(STORAGE_KEY)
  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, {
      detail: createDefaultCookieConsentChoices(),
    }),
  )
}

export function parseCookieConsentChoices(
  value: unknown,
): CookieConsentChoices | null {
  if (!value || typeof value !== 'object') return null

  const choices = createDefaultCookieConsentChoices()
  let hasKnownKey = false

  for (const [key, enabled] of Object.entries(value)) {
    if (!isOptionalCookieConsentItemId(key) || typeof enabled !== 'boolean') continue
    choices[key] = enabled
    hasKnownKey = true
  }

  return hasKnownKey ? choices : null
}
